import { IntlShape } from "react-intl";

import {
  GalleryMediaItem,
  MediaType,
  MediaNavigationItem,
} from "./photo-gallery.types";

export const MEDIA_TYPES = {
  ALL: "media",
  VIRTUAL_TOUR: "3d",
  VIDEOS: "videos",
  PHOTOS: "photos",
  VIDEO: "video",
} as const;

export const getMediaTypeLabels = (
  intl: IntlShape,
): Record<MediaType, string> => ({
  [MEDIA_TYPES.ALL]: intl.formatMessage({
    id: "listing.media_gallery.all_media",
    defaultMessage: "All media",
  }),
  [MEDIA_TYPES.VIRTUAL_TOUR]: intl.formatMessage({
    id: "listing.media_gallery.3d_tour",
    defaultMessage: "3D tour",
  }),
  [MEDIA_TYPES.VIDEOS]: intl.formatMessage({
    id: "listing.media_gallery.videos",
    defaultMessage: "Videos",
  }),
  [MEDIA_TYPES.PHOTOS]: intl.formatMessage({
    id: "listing.media_gallery.photos",
    defaultMessage: "Photos",
  }),
  [MEDIA_TYPES.VIDEO]: intl.formatMessage({
    id: "listing.media_gallery.video",
    defaultMessage: "Video",
  }),
});

/**
 * Determines which media types are available based on the provided media data
 */
export const getAvailableMediaTypes = (
  media: GalleryMediaItem[],
): MediaType[] => {
  const availableTypes: MediaType[] = [MEDIA_TYPES.ALL]; // Always show "All media"

  const hasPhotos = media.some(
    (item) =>
      item.embed_type !== MEDIA_TYPES.VIRTUAL_TOUR &&
      item.embed_type !== MEDIA_TYPES.VIDEO,
  );

  const hasVideos = media.some((item) => item.embed_type === MEDIA_TYPES.VIDEO);

  const hasVirtualTour = media.some(
    (item) => item.embed_type === MEDIA_TYPES.VIRTUAL_TOUR,
  );

  if (hasPhotos) {
    availableTypes.push(MEDIA_TYPES.PHOTOS);
  }

  if (hasVideos) {
    availableTypes.push(MEDIA_TYPES.VIDEOS);
  }

  if (hasVirtualTour) {
    availableTypes.push(MEDIA_TYPES.VIRTUAL_TOUR);
  }

  return availableTypes;
};

export const getMediaNavigationItems = (
  intl: IntlShape,
  media?: GalleryMediaItem[],
): readonly MediaNavigationItem[] => {
  const labels = getMediaTypeLabels(intl);
  const allItems = [
    { id: MEDIA_TYPES.ALL, label: labels[MEDIA_TYPES.ALL] },
    { id: MEDIA_TYPES.VIRTUAL_TOUR, label: labels[MEDIA_TYPES.VIRTUAL_TOUR] },
    { id: MEDIA_TYPES.VIDEOS, label: labels[MEDIA_TYPES.VIDEOS] },
    { id: MEDIA_TYPES.PHOTOS, label: labels[MEDIA_TYPES.PHOTOS] },
  ] as const;

  // If no media data provided, return all items (backward compatibility)
  if (!media) {
    return allItems;
  }

  // Filter items based on available media types
  const availableTypes = getAvailableMediaTypes(media);
  return allItems.filter((item) => availableTypes.includes(item.id));
};

export const isValidMediaType = (type: string): type is MediaType => {
  return Object.values(MEDIA_TYPES).includes(type as MediaType);
};
